import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sliders, Loader2, Package, X, Check, AlertTriangle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";

// Reason vocab mirrors backend STOCK_ADJUSTMENT_REASONS (routes/stock.py).
// Order matches the most likely UX flow — Damaged / Miscounted are top
// because they're the day-to-day cases; Other is last so it doesn't
// short-circuit the admin's thinking.
const REASON_OPTIONS = [
  { value: "damaged", label: "Damaged" },
  { value: "miscounted", label: "Miscounted" },
  { value: "found_extra", label: "Found extra" },
  { value: "expired", label: "Expired" },
  { value: "lost_stolen", label: "Lost / Stolen" },
  { value: "returned_to_supplier", label: "Returned to supplier" },
  { value: "other", label: "Other (notes required)" },
];

const STATUS_STYLE = {
  full: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  in_use: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  depleted: "bg-slate-700 text-slate-400 border-slate-600",
};

/**
 * Modal that lists every unit belonging to a product and lets an admin
 * override `remaining_quantity` directly. Distinct from the existing
 * Edit-delivery-line flow (which resets `initial_quantity` and is
 * refused on consumed units). This one works on any unit regardless of
 * consumption state — for real-world corrections like "roll shows 4m
 * remaining but there's actually none left."
 */
const AdjustStockLevelModal = ({ product, open, onClose, onAdjusted }) => {
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUnitId, setExpandedUnitId] = useState(null);
  const [newQty, setNewQty] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [reasonNotes, setReasonNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadUnits = useCallback(async () => {
    if (!product?.product_id) return;
    setIsLoading(true);
    try {
      // status=all so depleted units show too — admin might want to
      // re-open one that was mistakenly zeroed.
      const res = await axios.get(
        `${API}/stock/units?product_id=${product.product_id}&status=all`
      );
      setUnits(res.data || []);
    } catch (err) {
      console.error("Failed to load units:", err);
      toast.error(err.response?.data?.detail || "Failed to load units");
    } finally {
      setIsLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (open) {
      loadUnits();
      setExpandedUnitId(null);
      setNewQty("");
      setReasonCode("");
      setReasonNotes("");
    }
  }, [open, loadUnits]);

  const openAdjustForm = (unit) => {
    setExpandedUnitId(unit.unit_id);
    // Prefill with current value so a small correction is one keystroke.
    setNewQty(String(unit.remaining_quantity ?? 0));
    setReasonCode("");
    setReasonNotes("");
  };

  const closeAdjustForm = () => {
    setExpandedUnitId(null);
    setNewQty("");
    setReasonCode("");
    setReasonNotes("");
  };

  const submitAdjustment = async (unit) => {
    const parsed = parseFloat(newQty);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Enter a valid non-negative quantity");
      return;
    }
    if (!reasonCode) {
      toast.error("Please select a reason");
      return;
    }
    if (reasonCode === "other" && !reasonNotes.trim()) {
      toast.error("Notes are required when reason is 'Other'");
      return;
    }

    setIsSaving(true);
    try {
      const res = await axios.post(
        `${API}/stock/units/${unit.unit_id}/adjust-quantity`,
        {
          new_remaining_quantity: parsed,
          reason_code: reasonCode,
          reason_notes: reasonNotes.trim() || null,
        }
      );
      const delta = res.data?.delta ?? 0;
      const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;
      toast.success(`Adjusted (${deltaLabel}) — logged in audit trail`);
      closeAdjustForm();
      loadUnits();
      onAdjusted?.();
    } catch (err) {
      console.error("Adjust failed:", err);
      toast.error(err.response?.data?.detail || "Failed to adjust level");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="adjust-stock-level-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-saturn-orange" />
            Adjust Stock Level — {product?.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Direct correction of a unit's remaining quantity. Every change is
            audit-logged. For fixing the roll's initial size instead,
            use the Edit action in Purchases.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
          </div>
        ) : units.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>No units on file for this product.</p>
          </div>
        ) : (
          <div className="space-y-2 py-2">
            {units.map((unit) => {
              const isExpanded = expandedUnitId === unit.unit_id;
              const statusClass = STATUS_STYLE[unit.status] || STATUS_STYLE.depleted;
              return (
                <div
                  key={unit.unit_id}
                  className="bg-slate-800/50 rounded-lg border border-slate-700"
                  data-testid={`adjust-unit-row-${unit.unit_id}`}
                >
                  <div className="flex items-center justify-between p-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide ${statusClass}`}
                        >
                          {unit.status || "unknown"}
                        </span>
                        <span className="font-mono text-xs text-slate-400">
                          {unit.unit_id?.slice(-10)}
                        </span>
                        {unit.location && (
                          <span className="text-[10px] text-slate-500 uppercase">
                            {unit.location}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-300 mt-1">
                        <span className="text-slate-400">Remaining:</span>{" "}
                        <span className="font-semibold text-white">
                          {unit.remaining_display}
                        </span>
                        <span className="text-slate-500 mx-2">/</span>
                        <span className="text-slate-400">
                          initial {unit.initial_display}
                        </span>
                      </div>
                      {unit.last_adjustment_reason && (
                        <div className="text-[11px] text-amber-400/70 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Last adjusted: {unit.last_adjustment_reason.replace(/_/g, " ")}
                        </div>
                      )}
                    </div>
                    {!isExpanded && (
                      <Button
                        size="sm"
                        onClick={() => openAdjustForm(unit)}
                        className="bg-saturn-orange hover:bg-orange-600 flex-shrink-0"
                        data-testid={`adjust-level-btn-${unit.unit_id}`}
                      >
                        <Sliders className="w-3 h-3 mr-1" />
                        Adjust Level
                      </Button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-700 p-3 space-y-3 bg-slate-900/40">
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">
                          New remaining quantity
                          {unit.base_unit === "cm" && (
                            <span className="text-slate-500"> (cm — 100cm = 1m)</span>
                          )}
                          {unit.base_unit === "ml" && (
                            <span className="text-slate-500"> (ml)</span>
                          )}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newQty}
                          onChange={(e) => setNewQty(e.target.value)}
                          className="bg-slate-800 border-slate-600 text-white"
                          data-testid={`adjust-new-qty-${unit.unit_id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">
                          Reason
                        </Label>
                        <Select value={reasonCode} onValueChange={setReasonCode}>
                          <SelectTrigger
                            className="bg-slate-800 border-slate-600 text-white"
                            data-testid={`adjust-reason-select-${unit.unit_id}`}
                          >
                            <SelectValue placeholder="Select a reason…" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600 text-white">
                            {REASON_OPTIONS.map((r) => (
                              <SelectItem
                                key={r.value}
                                value={r.value}
                                data-testid={`adjust-reason-${r.value}`}
                              >
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">
                          Notes {reasonCode === "other" && <span className="text-amber-400">(required)</span>}
                        </Label>
                        <Input
                          value={reasonNotes}
                          onChange={(e) => setReasonNotes(e.target.value)}
                          placeholder="Optional — details of the correction"
                          className="bg-slate-800 border-slate-600 text-white"
                          data-testid={`adjust-notes-${unit.unit_id}`}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={closeAdjustForm}
                          disabled={isSaving}
                          className="border-slate-600 text-slate-300"
                          data-testid={`adjust-cancel-${unit.unit_id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => submitAdjustment(unit)}
                          disabled={isSaving}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`adjust-save-${unit.unit_id}`}
                        >
                          {isSaving ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <Check className="w-3 h-3 mr-1" />
                          )}
                          Save adjustment
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-600 text-slate-300"
            data-testid="adjust-modal-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustStockLevelModal;
